/* eslint-disable no-alert */
import {
    useEffect, useState, useRef, useMemo
} from 'react';

import styled from '@emotion/styled';
import {
    colors, trimChar, heapTrack, FullAddress,
} from '@modernanimal/common';
import { formatCardNumber, formatExpirationDate } from '@modernanimal/common/src/utils/clientOnboarding';
import Modal from '@modernanimal/common/src/web/atoms/Modal';
import PlacesInputField, { PlacesInputFieldRefType } from '@modernanimal/common/src/web/PlacesInputField';
import creditCardType from 'credit-card-type';
import { CreditCardTypeCardBrandId } from 'credit-card-type/src/types';
import { DateTime } from 'luxon';
import posthog from 'posthog-js';
import { useSearchParams } from 'react-router-dom';
import { useBreakpoint } from 'use-breakpoint';

import { createCreditCard } from './clientOnboarding.helpers';
import MembershipSelection from './MembershipSelection';
import {
    CreditCardInput, CustomerMembershipTierEnum,
} from '../__generated__/graphql';
import InputField from '../components/InputField';
import LoadingModal from '../components/LoadingModal';
import PrimaryButton from '../components/PrimaryButton';
import Fonts from '../fonts';
import { BREAKPOINTS, trackGTMPurchaseEvent } from '../helpers';
import Icons from '../icons';
import {
    calculatePromoDiscountAmount, createPatientRelatedEntities, getMembershipProductsAndComprehensiveExam,
    mapMembershipProducts, Membership
} from '../patientOnboarding/patientOnboarding.helpers';
import useWebBookingStore from '../store';

type Props = {
    goToNextStep: ()=> void;
};

const AddCard = ({ goToNextStep }: Props) => {
    const {
        client, patient, externalClinic, isMedicalRecordRequested, updateOnGoToPrevStep, appointment, updateAppointment,
        isCreditCardCreated, updateIsCreditCardCreated,
        isMembershipCreated, updateIsMembershipCreated, updatePatient, updateIsMedicalRecordRequested,
    } = useWebBookingStore((state) => ({
        client: state.client,
        patient: state.patient,
        updatePatient: state.setPatient,
        externalClinic: state.externalClinic,
        updateOnGoToPrevStep: state.setOnGoToPrevStep,
        appointment: state.appointment,
        updateAppointment: state.setAppointment,
        isCreditCardCreated: state.isCreditCardCreated,
        updateIsCreditCardCreated: state.setIsCreditCardCreated,
        isMedicalRecordRequested: state.isMedicalRecordRequested,
        updateIsMedicalRecordRequested: state.setIsMedialRecordsRequested,
        isMembershipCreated: state.isMembershipCreated,
        updateIsMembershipCreated: state.setIsMembershipCreated,
    }));
    const { breakpoint } = useBreakpoint(BREAKPOINTS, 'desktop');
    const isMobile = breakpoint === 'mobile';

    // UI
    const [loading, setLoading] = useState(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [isSelectingMembership, setIsSelectingMembership] = useState(false);
    const [membershipPlans, setMembershipPlans] = useState<Membership[]>([]);

    const [cardNumber, setCardNumber] = useState('');
    const [expirationDate, setExpirationDate] = useState('');
    const [cvv, setCvv] = useState('');
    const [cardType, setCardType] = useState<CreditCardTypeCardBrandId | null>(null);
    const [billingAddressIsSame, setBillingAddressIsSame] = useState(true);
    const [isExpirationDateValid, setIsExpirationDateValid] = useState(true);
    const [isCreditCardValid, setIsCreditCardValid] = useState(true);

    const [selectedBillingAddress, setSelectedBillingAddress] = useState<Pick<google.maps.places.PlacePrediction, 'text' | 'placeId'> | null>(null);
    const [billingAddress, setBillingAddress] = useState<FullAddress>({
        address: '',
        apartment: '',
        city: '',
        state: '',
        zipcode: ''
    });

    const [searchParams] = useSearchParams();
    const locationId = searchParams.get('location');

    useEffect(() => {
        const getMembershipPlans = async () => {
            const data = await getMembershipProductsAndComprehensiveExam(
                client.token, locationId || appointment.slot?.location.id as string,
            );
            const memberships = mapMembershipProducts(data);
            if (patient.promo?.amountOff) {
                setMembershipPlans(memberships.map((plan) => ({
                    ...plan,
                    discountedPrice: calculatePromoDiscountAmount(patient.promo, plan.membershipPrice)
                })));
            } else {
                setMembershipPlans(memberships);
            }
        };

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        getMembershipPlans();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client.token, appointment.slot?.location.id, patient.promo, locationId]);

    useEffect(() => {
        if (patient.promo?.amountOff) {
            setMembershipPlans(membershipPlans.map((plan) => ({
                ...plan,
                discountedPrice: calculatePromoDiscountAmount(patient.promo, plan.membershipPrice)
            })));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const allAccessPlan = useMemo(
        () => membershipPlans.find(
            (plan) => plan.membershipTier === CustomerMembershipTierEnum.AllAccess
        ), [membershipPlans]
    );
    const essentialPlan = useMemo(
        () => membershipPlans.find(
            (plan) => plan.membershipTier === CustomerMembershipTierEnum.Essential
        ), [membershipPlans]
    );
    const allAccessPricePaid = useMemo(
        () => allAccessPlan?.discountedPrice || allAccessPlan?.membershipPrice || '0', [allAccessPlan]
    );
    const essentialPricePaid = useMemo(
        () => essentialPlan?.discountedPrice || essentialPlan?.membershipPrice || '0', [essentialPlan]
    );
    const payAsYouGoPrice = useMemo(
        () => membershipPlans.find(
            (plan) => plan.membershipTier === CustomerMembershipTierEnum.PayAsYouGo
        )?.membershipPrice || '0', [membershipPlans]
    );

    const placesInputFieldRef = useRef<PlacesInputFieldRefType>();

    const isPayAsYouGo = patient.membershipTier === CustomerMembershipTierEnum.PayAsYouGo;
    const isEssential = patient.membershipTier === CustomerMembershipTierEnum.Essential;
    const isAllAccess = patient.membershipTier === CustomerMembershipTierEnum.AllAccess;

    const hasBillingAddress = () => !!(
        billingAddress.address && billingAddress.city && billingAddress.state && billingAddress.zipcode
    ) || billingAddressIsSame;
    const isCardNumberEntered = () => cardNumber.replaceAll(' ', '').length >= 15;
    const isValidInfo = () => isCardNumberEntered() && expirationDate.length === 5 && !!cvv
        && isExpirationDateValid && isCreditCardValid && hasBillingAddress();

    const getMembershipName = (membership: string) => {
        switch (membership) {
        case CustomerMembershipTierEnum.AllAccess:
            return 'All Access';
        case CustomerMembershipTierEnum.Essential:
            return 'Essential';
        case CustomerMembershipTierEnum.PayAsYouGo:
            return 'Pay As You Go';
        default:
            return '';
        }
    };

    const headerTitle = isPayAsYouGo ? 'Add card' : 'Confirm Membership';
    const headerDescription = isPayAsYouGo
        ? `To reserve your appointment, add a card. Exams cost $${payAsYouGoPrice}; you will not be charged until you complete your visit.`
        : `Add a card to activate your ${getMembershipName(patient.membershipTier)} membership. Membership will automatically renew in one year.`;

    const changeCreditCardNumber = (cardNumber: string) => {
        if (!isCreditCardValid) setIsCreditCardValid(true);

        const formattedCardNumber = formatCardNumber(cardNumber);
        setCardNumber(formattedCardNumber);

        const cardTypes = creditCardType(cardNumber);
        if (cardTypes.length === 1) {
            // Update card type to determine CVV max length
            setCardType(cardTypes[0].type as CreditCardTypeCardBrandId);
        }
    };

    const changeCvv = (cvv: string) => {
        if (!isCreditCardValid) setIsCreditCardValid(true);

        setCvv(trimChar(cvv));
    };

    const changeExpirationDate = (expirationDate: string) => {
        const formattedExpirationDate = formatExpirationDate(expirationDate);
        setExpirationDate(formattedExpirationDate);

        const expirationDateTime = DateTime.fromFormat(formattedExpirationDate, 'LL/yy').endOf('month');
        if (!expirationDateTime.invalidReason) {
            setIsExpirationDateValid(expirationDateTime >= DateTime.local());
        }
    };

    const confirmBooking = async () => {
        setLoading(true);
        // Create card first to prevent ghost objects from being created
        // in the case of an invalid credit card
        const hasCreatedCreditCard = await createCreditCardObject();
        if (!hasCreatedCreditCard) {
            setLoading(false);
            return;
        }

        const createdAppointmentId = await createPatientRelatedEntities(
            patient,
            client,
            externalClinic,
            isMedicalRecordRequested,
            isMembershipCreated,
            appointment,
            updatePatient,
            updateIsMedicalRecordRequested,
            updateIsMembershipCreated,
            updateAppointment,
            locationId as string
        );
        if (!createdAppointmentId) {
            setLoading(false);
            return;
        }

        heapTrack('Web Booking - Add Card View - Confirm Booking Button Clicked');
        posthog.capture('web_booking_click', { screen: 'AddCard', action: 'ConfirmBookingButtonClicked' });
        setLoading(false);

        if (patient.membershipTier === CustomerMembershipTierEnum.AllAccess) {
            setIsConfirmationModalOpen(true);
            trackGTMPurchaseEvent(
                createdAppointmentId,
                CustomerMembershipTierEnum.AllAccess,
                allAccessPricePaid || '0'
            );
        } else if (isEssential) {
            setIsConfirmationModalOpen(true);
            trackGTMPurchaseEvent(
                createdAppointmentId,
                CustomerMembershipTierEnum.Essential,
                essentialPricePaid || '0'
            );
        } else {
            trackGTMPurchaseEvent(createdAppointmentId, CustomerMembershipTierEnum.PayAsYouGo);
            goToNextStep();
        }
    };

    const createCreditCardObject = async (): Promise<boolean> => {
        if (isCreditCardCreated) return true;

        try {
            const fullAddress = billingAddressIsSame ? client.fullAddress : billingAddress;
            const creditCardInput: CreditCardInput = {
                creditCardNumber: cardNumber,
                expirationDate: DateTime.fromFormat(expirationDate, 'mm/yy').toFormat('yyyy-mm-dd'),
                cvv,
                household: { set: client.household.id },
                billingAddress: fullAddress.address,
                billingApartment: fullAddress.apartment,
                billingCity: fullAddress.city,
                billingState: fullAddress.state,
                billingZipcode: fullAddress.zipcode,
            };
            const createdCreditCard = await createCreditCard(client.token, creditCardInput);
            setIsCreditCardValid(true);
            updateIsCreditCardCreated(true);
            heapTrack('Web Booking - Add Card View - Card Added');
            posthog.capture('web_booking_action', { screen: 'AddCard', action: 'CardAdded' });
            return !!createdCreditCard;
        } catch (err) {
            setIsCreditCardValid(false);
            setLoading(false);
            return false;
        }
    };

    const selectBillingAddress = async (prediction: Pick<google.maps.places.PlacePrediction, 'text' | 'placeId'>) => {
        setSelectedBillingAddress(prediction);
        await placesInputFieldRef.current?.formatAddress(
            prediction,
            billingAddress.apartment,
            setBillingAddress,
            () => { }
        );
    };

    const switchMembershipTiers = () => {
        updateOnGoToPrevStep(() => setIsSelectingMembership(false));
        setIsSelectingMembership(true);
    };

    const validateExpirationDate = () => {
        const expirationDateTime = DateTime.fromFormat(expirationDate, 'LL/yy').endOf('month');
        setIsExpirationDateValid(!expirationDateTime.invalidReason && expirationDateTime >= DateTime.local());
    };

    if (isSelectingMembership) {
        return (
            <MembershipSelection
                membershipPlans={membershipPlans}
                onSelectMembershipTier={() => {
                    setIsSelectingMembership(false);
                }}
            />
        );
    }

    const renderCardInfo = () => {
        const BillingAddressFont = billingAddressIsSame ? Fonts.LabelMedium : Fonts.Body;
        const renderBillingAddressSameButton = () => (
            <BillingAddressButton onClick={() => setBillingAddressIsSame(!billingAddressIsSame)}>
                <BillingAddressFont style={{ color: billingAddressIsSame ? colors.GREY_1 : colors.GREY_2 }}>
                    Billing address is the same as my home address
                </BillingAddressFont>
                {billingAddressIsSame ? (
                    <Icons.CircleCheck height={24} width={24} fill={colors.GREEN} />
                ) : (
                    <EmptyCircle />
                )}
            </BillingAddressButton>
        );

        return (
            <CreditCardInfo>
                <Fonts.LabelMedium>Card number</Fonts.LabelMedium>
                <CardNumberInputContainer>
                    <InputField
                        value={cardNumber}
                        placeholder="Enter card number"
                        onChange={changeCreditCardNumber}
                        inputMode="numeric"
                        autoFocus
                        isInvalid={!isCreditCardValid}
                    />
                    {!isCreditCardValid && (
                        <IconContainer>
                            <Icons.ExclamationCircleRed height={24} width={24} fill={colors.ERROR_RED} />
                        </IconContainer>
                    )}
                </CardNumberInputContainer>
                {!isCreditCardValid && (
                    <Fonts.BodySmall style={{ color: colors.ERROR_RED }}>
                        We were unable to verify your card information.
                        <br />
                        Please try again.
                    </Fonts.BodySmall>
                )}
                <Row>
                    <CreditCardInfo>
                        <InputField
                            value={expirationDate}
                            placeholder="MM/YY"
                            onChange={changeExpirationDate}
                            onBlur={validateExpirationDate}
                            inputMode="numeric"
                        />
                        {!isExpirationDateValid && (
                            <Fonts.BodySmall style={{ color: colors.ERROR_RED }}>
                                Invalid expiration date
                            </Fonts.BodySmall>
                        )}
                    </CreditCardInfo>
                    <CreditCardInfo>
                        <InputField
                            value={cvv}
                            placeholder="CVV"
                            onChange={changeCvv}
                            maxLength={cardType === 'american-express' ? 4 : 3}
                            inputMode="numeric"
                        />
                    </CreditCardInfo>
                </Row>
                {!billingAddressIsSame && (
                    <PlacesInputField
                        ref={placesInputFieldRef}
                        placeholder="Billing address"
                        hasChanged={!!billingAddress.address}
                        showError={false}
                        selectedValue={selectedBillingAddress}
                        onSelectResult={selectBillingAddress}
                        autoFocus
                        types={['street_address']}
                    />
                )}
                {renderBillingAddressSameButton()}
                {
                    loading && (
                        <LoadingModal isVisible={loading} patientName={patient.name} />
                    )
                }
            </CreditCardInfo>
        );
    };

    const renderSwitchPlan = () => {
        const headerText = 'Change Membership Plan';
        return (
            <SwitchPlanButton onClick={switchMembershipTiers}>
                <SwitchPlanHeader>
                    <Fonts.LabelMedium>{headerText}</Fonts.LabelMedium>
                    <Icons.ChevronRight width={18} height={18} fill={colors.GREY_1} />
                </SwitchPlanHeader>
            </SwitchPlanButton>
        );
    };

    // eslint-disable-next-line complexity
    const renderPaymentSummary = () => {
        const hasDiscount = !!patient.promo?.amountOff;
        const showDiscountedPrice = hasDiscount && !isPayAsYouGo;
        const showAccountCredit = hasDiscount && isPayAsYouGo;

        const dueNowPrice = isEssential
            ? essentialPlan?.membershipPrice
            : allAccessPlan?.membershipPrice;

        const discountedPrice = isEssential
            ? essentialPlan?.discountedPrice
            : allAccessPlan?.discountedPrice;

        return (
            <PaymentSummaryContainer data-testid="payment-summary-container">
                <Fonts.LabelMedium>Payment Summary</Fonts.LabelMedium>
                <PaymentSummaryBody>
                    <Fonts.Body style={{ color: colors.GREY_2 }}>
                        Due now
                    </Fonts.Body>
                    <DueNowPriceContainer>
                        <Fonts.Body
                            style={{ color: colors.GREY_2, textDecoration: showDiscountedPrice ? 'line-through' : undefined }}
                        >
                            {isPayAsYouGo ? '$0' : `$${dueNowPrice}`}
                        </Fonts.Body>
                        {showDiscountedPrice && (
                            <Fonts.Body style={{ color: colors.GREEN }}>
                                {`$${discountedPrice}`}
                            </Fonts.Body>
                        )}
                    </DueNowPriceContainer>

                </PaymentSummaryBody>
                {showAccountCredit && (
                    <Fonts.Body style={{ color: colors.GREEN, textAlign: 'right' }}>
                        +
                        {' '}
                        {`$${patient.promo?.amountOff} credit`}
                    </Fonts.Body>
                )}
            </PaymentSummaryContainer>
        );
    };

    const renderFooter = () => {
        const isEssentialFree = isEssential && essentialPricePaid === '0';
        const isAllAccessFree = isAllAccess && allAccessPricePaid === '0';
        const buttonText = isPayAsYouGo || isEssentialFree || isAllAccessFree
            ? 'Confirm Booking'
            : `Confirm Payment of $${isEssential ? essentialPricePaid : allAccessPricePaid}`;

        return (
            <Footer isMobile={isMobile}>
                <PrimaryButton
                    id="confirm-payment-button"
                    color="periwinkle"
                    onClick={confirmBooking}
                    disabled={!isValidInfo()}
                    loading={loading}
                >
                    {buttonText}
                </PrimaryButton>
                <Fonts.BodySmall style={{ color: colors.GREY_2, textAlign: 'center' }}>
                    A card is needed in case of any no-show or late cancellation and may incur a $50 fee.
                </Fonts.BodySmall>
            </Footer>
        );
    };

    const renderConfirmationModal = () => (
        <Modal
            isModalOpen={isConfirmationModalOpen}
            onCloseModal={() => setIsConfirmationModalOpen(false)}
        >
            <ConfirmationModalContainer data-testid="membership-confirmation-modal">
                <Icons.CircleCheck height={64} width={64} fill={colors.GREEN} />
                <ConfirmationModalBody>
                    <Fonts.Heading06>
                        Your
                        {' '}
                        {isEssential ? 'Essential' : 'All Access'}
                        {' '}
                        membership is confirmed
                    </Fonts.Heading06>
                    <Fonts.Body style={{ color: colors.GREY_2 }}>
                        {`Confirmation of your $${isEssential ? essentialPricePaid : allAccessPricePaid} payment has been emailed to you.`}
                    </Fonts.Body>
                </ConfirmationModalBody>
                <PrimaryButton
                    id="add-card-button"
                    color="black"
                    onClick={() => {
                        setIsConfirmationModalOpen(false);
                        goToNextStep();
                    }}
                >
                    View appointment
                </PrimaryButton>
            </ConfirmationModalContainer>
        </Modal>
    );

    return (
        <AddCardContainer>
            <Body>
                <Subbody>
                    <Fonts.Heading04>{headerTitle}</Fonts.Heading04>
                    <Fonts.Body style={{ color: colors.GREY_2 }}>{headerDescription}</Fonts.Body>
                </Subbody>
                {renderCardInfo()}
                <Divider />
                {renderSwitchPlan()}
                {renderPaymentSummary()}
            </Body>
            {renderFooter()}
            {renderConfirmationModal()}
        </AddCardContainer>
    );
};

export default AddCard;

const AddCardContainer = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: 32px 24px 0 24px;
    gap: 8px;
    justify-content: space-between;
    position: relative;
`;

const BillingAddressButton = styled.button`
    display: flex;
    flex-direction: row;
    gap: 6px;
    padding: 14px 16px;
    border: 1px solid ${colors.PERIWINKLE_4};
    border-radius: 8px;
    justify-content: space-between;
    text-align: left;
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const CardNumberInputContainer = styled.div`
    display: flex;
    position: relative;
    align-items: center;
`;

const ConfirmationModalBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    text-align: center;
`;

const ConfirmationModalContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 300px;
    padding: 16px;
    border-radius: 16px;
    background-color: ${colors.WHITE};
    align-items: center;
`;

const CreditCardInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
`;

const Divider = styled.div`
    display: flex;
    width: 100%;
    height: 1px;
    background-color: ${colors.PERIWINKLE_4};
`;

const DueNowPriceContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
`;

const EmptyCircle = styled.div`
    display: flex;
    height: 22px;
    width: 22px;
    border-radius: 12px;
    border: 1px solid ${colors.PERIWINKLE_4};
`;

const Footer = styled.div<{ isMobile: boolean }>`
    display: flex;
    text-align: center;
    flex-direction: column;
    gap: 12px;
    position: ${({ isMobile }) => (isMobile ? 'sticky' : 'static')};
    bottom: 0;
    background-color: ${colors.WHITE};
    padding: 16px 0 32px 0;
`;

const IconContainer = styled.div`
    position: absolute;
    right: 12px;
`;

const PaymentSummaryBody = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
`;

const PaymentSummaryContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px 16px;
    border-radius: 10px;
    background-color: ${colors.GREY_7};
`;

const Row = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
`;

const Subbody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const SwitchPlanButton = styled.button`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px 16px;
    border: 1px solid ${colors.PERIWINKLE_4};
    border-radius: 8px;
    background-color: ${colors.PERIWINKLE_6};
`;

const SwitchPlanHeader = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
`;
